import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Input,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { IoTrashOutline as DeleteIcon } from 'react-icons/io5';
import { defineMessages, useIntl } from 'react-intl';
import styles from '../IntegrationsListPage.css';
import HubspotPropertyAutocomplete from './HubspotPropertyAutocomplete';

const messages = defineMessages({
  modalTitle: {
    id: 'IntegrationsSettings.EditFetchProperties',
    defaultMessage: 'Edit Properties to Fetch',
  },
  hubpotProperty: {
    id: 'IntegrationsSettings.HubspotProperty',
    defaultMessage: 'Hubspot Property',
  },
  propertyType: {
    id: 'IntegrationsSettings.FetchPropertyType',
    defaultMessage: 'Type',
  },
  propertyDisplayName: {
    id: 'IntegrationsSettings.FetchPropertyDisplayName',
    defaultMessage: 'Display Name',
  },
  addProperty: {
    id: 'IntegrationsSettings.AddFetchProperty',
    defaultMessage: 'Add Property',
  },
  removeProperty: {
    id: 'IntegrationsSettings.RemoveFetchProperty',
    defaultMessage: 'Remove',
  },
  cancel: {
    id: 'Confirm.Cancel',
    defaultMessage: 'Cancel',
  },
  save: {
    id: 'Confirm.Save',
    defaultMessage: 'Save',
  },
  typeTerms: {
    id: 'IntegrationsSettings.FetchPropertyTypeTerms',
    defaultMessage: 'Terms',
  },
  typeNumber: {
    id: 'IntegrationsSettings.FetchPropertyTypeNumber',
    defaultMessage: 'Number',
  },
  typeDate: {
    id: 'IntegrationsSettings.FetchPropertyTypeDate',
    defaultMessage: 'Date',
  },
  keyRequired: {
    id: 'IntegrationsSettings.FetchPropertyKeyRequired',
    defaultMessage: 'Key is required',
  },
  keyDuplicate: {
    id: 'IntegrationsSettings.FetchPropertyKeyDuplicate',
    defaultMessage: 'Key must be unique',
  },
  typeRequired: {
    id: 'IntegrationsSettings.FetchPropertyTypeRequired',
    defaultMessage: 'Type is required',
  },
  displayNameRequired: {
    id: 'IntegrationsSettings.FetchPropertyDisplayNameRequired',
    defaultMessage: 'Display name is required',
  },
  noPropertiesAdded: {
    id: 'IntegrationsSettings.noPropertiesAdded',
    defaultMessage: 'No properties added',
  },
});

const typeOptions = [
  { value: 'terms', labelId: messages.typeTerms },
  { value: 'number', labelId: messages.typeNumber },
  { value: 'date', labelId: messages.typeDate },
];

function FetchPropertiesModal({ open, onClose, fetchProperties, onSave }) {
  const { formatMessage } = useIntl();
  const [properties, setProperties] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setProperties(
        Object.entries(fetchProperties || {}).map(([key, value]) => ({
          key,
          type: value.type || '',
          displayName: value.displayName || value.dislayName || '',
        })),
      );
    } else {
      setProperties([]);
    }
  }, [fetchProperties, open]);

  const validate = values => {
    const errs = {};
    const keys = values.map(p => p.key.trim());
    values.forEach((p, idx) => {
      if (!p.key.trim()) errs[`${idx}-key`] = formatMessage(messages.keyRequired);
      else if (keys.filter(k => k === p.key.trim()).length > 1)
        errs[`${idx}-key`] = formatMessage(messages.keyDuplicate);
      if (!p.type) errs[`${idx}-type`] = formatMessage(messages.typeRequired);
      if (!p.displayName.trim())
        errs[`${idx}-displayName`] = formatMessage(messages.displayNameRequired);
    });
    return errs;
  };

  const handleChange = idx => e => {
    const { name, value } = e.target;
    const newProps = properties.map((p, i) => (i === idx ? { ...p, [name]: value } : p));
    setProperties(newProps);
    setErrors(validate(newProps));
  };

  const handleAdd = () => {
    const newProps = [...properties, { key: '', type: 'terms', displayName: '' }];
    setProperties(newProps);
    setErrors(validate(newProps));
    setTimeout(() => {
      document.querySelector(`#key-${newProps.length - 1} input:first-of-type`).focus();
    }, 10);
  };

  const handleRemove = idx => () => {
    const newProps = properties.filter((_, i) => i !== idx);
    setProperties(newProps);
    setErrors(validate(newProps));
  };

  const handleSave = () => {
    const errs = validate(properties);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const obj = {};
    properties.forEach(p => {
      obj[p.key.trim()] = { type: p.type, displayName: p.displayName.trim() };
    });
    onSave(obj);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{formatMessage(messages.modalTitle)}</DialogTitle>
      <DialogContent sx={{ pb: 0, display: 'flex', flexDirection: 'column' }}>
        <Box className={styles.syncSurveysSection}>
          <Box className={styles.syncSurveysAddBtn}>
            <Button
              onClick={handleAdd}
              color="primary"
              variant="text"
              startIcon={<AddIcon />}
              aria-label={formatMessage(messages.addProperty)}
              className={styles.syncSurveysAddBtnInner}
            >
              {formatMessage(messages.addProperty)}
            </Button>
          </Box>
          <Card variant="outlined" className={styles.syncSurveysTable}>
            <Box className={styles.syncSurveysHeader}>
              <Box className={styles.fetchPropertiesHeaderHsProperty}>
                {formatMessage(messages.hubpotProperty)}
              </Box>
              <Box className={styles.fetchPropertiesHeaderType}>
                {formatMessage(messages.propertyType)}
              </Box>
              <Box className={styles.fetchPropertiesHeaderDisplayName}>
                {formatMessage(messages.propertyDisplayName)}
              </Box>
              <Box className={styles.syncSurveysHeaderActionsHs} />
            </Box>
            <Box>
              {properties.length === 0 ? (
                <Box className={styles.syncSurveysEmptyState}>
                  <Typography variant="body2" color="textSecondary">
                    {formatMessage(messages.noPropertiesAdded)}
                  </Typography>
                </Box>
              ) : (
                properties.map((prop, idx) => (
                  <Box key={prop.key} className={styles.syncSurveysRow} id={`key-${idx}`}>
                    <HubspotPropertyAutocomplete
                      value={prop.key}
                      onChange={(_, option) => {
                        const newProps = properties.map((p, i) => {
                          if (i !== idx) return p;
                          if (!option) return { ...p, key: '', displayName: p.displayName };
                          const key = option.name;
                          let { displayName } = p;
                          if (!displayName && option.label) displayName = option.label;
                          return { ...p, key, displayName };
                        });
                        setProperties(newProps);
                        setErrors(validate(newProps));
                        if (option && !prop.displayName && option.label) {
                          setTimeout(() => {
                            const row = document.getElementById(`key-${idx}`);
                            if (row) {
                              const displayNameInput = row.querySelector(
                                'input[name="displayName"]',
                              );
                              if (displayNameInput) displayNameInput.focus();
                            }
                          }, 0);
                        }
                      }}
                      defaultLabel={formatMessage(messages.hubpotProperty)}
                      className={styles.fetchPropertiesRowHsProperty}
                      error={!!errors[`${idx}-key`]}
                      helperText={errors[`${idx}-key`] || ' '}
                    />
                    <FormControl
                      variant="standard"
                      className={styles.fetchPropertiesRowType}
                      error={!!errors[`${idx}-type`]}
                      helperText={errors[`${idx}-type`] || ' '}
                    >
                      <Select
                        name="type"
                        value={prop.type}
                        onChange={handleChange(idx)}
                        inputProps={{
                          'aria-label': formatMessage(messages.propertyType),
                        }}
                        input={<Input disableUnderline />}
                      >
                        <MenuItem value="" disabled>
                          {formatMessage(messages.propertyType)}
                        </MenuItem>
                        {typeOptions.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {formatMessage(opt.labelId)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      variant="standard"
                      className={styles.fetchPropertiesRowDisplayName}
                      name="displayName"
                      value={prop.displayName}
                      onChange={handleChange(idx)}
                      error={!!errors[`${idx}-displayName`]}
                      helperText={errors[`${idx}-displayName`] || ' '}
                      placeholder={formatMessage(messages.propertyDisplayName)}
                      InputProps={{ disableUnderline: true }}
                    />
                    <Box className={styles.syncSurveysRowActionsHs}>
                      <IconButton
                        color="error"
                        onClick={handleRemove(idx)}
                        size="small"
                        aria-label={formatMessage(messages.removeProperty)}
                        title={formatMessage(messages.removeProperty)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Card>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{formatMessage(messages.cancel)}</Button>
        <Button onClick={handleSave} variant="contained">
          {formatMessage(messages.save)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

FetchPropertiesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fetchProperties: PropTypes.object,
  onSave: PropTypes.func.isRequired,
};

FetchPropertiesModal.defaultProps = {
  fetchProperties: {},
};

export default FetchPropertiesModal;
